import { formatMoney, formatUtcDateTimeToTimeZone } from '@belong/common';
import { Tabs, Text, Tooltip } from '@belong/ui';
import clsx from 'clsx';
import isPast from 'date-fns/isPast';
import { UnitListingDetailsModel } from 'src/api/models';
import { HOUSE_PAGE_STRINGS } from '../../house-page.strings';
import css from './deposit-tabs.module.css';

type DepositTabsProps = {
  home: UnitListingDetailsModel;
};

const LEASE_DEFAULT_MONTHS = 12;

export function DepositTabs({ home }: DepositTabsProps): JSX.Element {
  const { listingInfo, basicInfo, minLiabilityCoverage, maxLiabilityCoverage } =
    home;
  const {
    availableOn,
    leaseMinMonths,
    leaseMaxMonths,
    depositAmount,
    depositAmountAlternative,
    petInfo,
  } = listingInfo || {};
  const { timeZone } = basicInfo || {};

  let leaseTerm = String(leaseMinMonths);

  // `?? 0` is not a behaviour change: `undefined > 12` is already false.
  if ((leaseMaxMonths ?? 0) > LEASE_DEFAULT_MONTHS) {
    leaseTerm = `${leaseTerm} - ${leaseMaxMonths}`;
  }

  const withDepositItems = [];
  const depositFreeItems = [];

  // Lease Term item
  withDepositItems.push({ name: 'Lease Term', value: `${leaseTerm} Months` });
  depositFreeItems.push({ name: 'Lease Term', value: `${leaseTerm} Months` });

  // Available item
  if (availableOn) {
    if (isPast(new Date(availableOn))) {
      withDepositItems.push({ name: 'Available', value: 'Immediately' });
      depositFreeItems.push({ name: 'Available', value: 'Immediately' });
    } else {
      const availableItem = {
        name: 'Available',
        value: formatUtcDateTimeToTimeZone({
          dateTime: availableOn,
          format: 'MMM d, yyyy',
          timeZone,
        }),
      };
      withDepositItems.unshift(availableItem);
      depositFreeItems.unshift(availableItem);
    }
  }

  // Deposit item
  withDepositItems.push({
    name: 'Deposit Amount',
    value: formatMoney(depositAmount || 0),
  });
  depositFreeItems.push({
    name: (
      <div className="flex justify-center">
        <div className="mr-2xs">Deposit-Free Fee</div>
        <div className="flex-none">
          <Tooltip
            arrowProps={{ className: 'fill-dark-navy' }}
            contentProps={{ side: 'top' }}
            content={
              <div className="bg-dark-navy p-2sm max-w-xs rounded">
                <Text className="text-white">
                  {HOUSE_PAGE_STRINGS['depositTabs.depositFree.tooltip']}
                </Text>
              </div>
            }
          />
        </div>
      </div>
    ),
    value: `${formatMoney(depositAmountAlternative || 0)}/Mo.`,
  });

  // Pet item
  if (petInfo && petInfo.isPetFriendly) {
    const {
      depositAmount,
      depositOption,
      depositAmountAlternative,
      feeOption,
    } = petInfo;
    withDepositItems.push({
      name: 'Pet Deposit',
      value: `${formatMoney(depositAmount || 0)}${depositOption === 'PerPet' ? ' Per Pet' : ''}`,
    });
    depositFreeItems.push({
      name: '+Pet Fee',
      value: `${formatMoney(depositAmountAlternative || 0)}/Mo.${feeOption === 'PerPet' ? ' Per Pet' : ''}`,
    });
  }

  // Liability Coverage item
  const liabilityCoverageItem = {
    name: (
      <div className="flex justify-center">
        <div className="mr-2xs">Liability Coverage</div>
        <div className="flex-none">
          <Tooltip
            arrowProps={{ className: 'fill-dark-navy' }}
            contentProps={{ side: 'top' }}
            content={
              <div className="bg-dark-navy p-2sm max-w-xs rounded">
                <Text className="text-white">
                  {HOUSE_PAGE_STRINGS['depositTabs.liabilityCoverage.tooltip']}
                </Text>
              </div>
            }
          />
        </div>
      </div>
    ),
    value:
      minLiabilityCoverage === maxLiabilityCoverage
        ? `${formatMoney(minLiabilityCoverage)}/Mo.`
        : `${formatMoney(minLiabilityCoverage)}-${formatMoney(maxLiabilityCoverage)}/Mo.`,
  };

  withDepositItems.push(liabilityCoverageItem);
  depositFreeItems.push(liabilityCoverageItem);

  return (
    <div className="relative w-full">
      <Tabs activeColor="bg-green">
        <Tabs.TabList className="block text-center">
          <Tabs.TabItem index={0}>With Deposit</Tabs.TabItem>
          <Tabs.TabItem index={1}>
            <div className="relative">
              Deposit-Free
              {/* TODO: Ask Randy if we still want this NEW tag */}
              <div
                className={clsx(css.tabTag, 'rounded-small bg-green px-2xs')}
              >
                <Text variant="p1" fontWeight="semibold" className="text-white">
                  NEW
                </Text>
              </div>
            </div>
          </Tabs.TabItem>
        </Tabs.TabList>

        <Tabs.PanelList>
          <Tabs.PanelItem>
            {withDepositItems.map((listingItem, idx) => (
              <div className="mb-xs flex justify-between last:mb-0" key={idx}>
                <div className="pr-xs text-p1 font-semibold">
                  {listingItem.name}
                </div>
                <div className="text-p1 text-right">{listingItem.value}</div>
              </div>
            ))}
          </Tabs.PanelItem>
          <Tabs.PanelItem>
            {depositFreeItems.map((listingItem, idx) => (
              <div className="mb-xs flex justify-between last:mb-0" key={idx}>
                <div className="pr-xs text-p1 font-semibold">
                  {listingItem.name}
                </div>
                <div className="text-p1 text-right">{listingItem.value}</div>
              </div>
            ))}
          </Tabs.PanelItem>
        </Tabs.PanelList>
      </Tabs>
    </div>
  );
}
